import { cloneDeep } from 'lodash';

import { type Obj } from '~/common/types';

import { createKeyGuard } from './guard';

type CollectionInput<TKey extends Key, TValue> = CollectionRecord<TKey, TValue> | Map<TKey, TValue>;

type CollectionRecord<TKey extends Key = Key, TValue = unknown> = { [Key in TKey]: TValue };

type Key = string | number | symbol;

type WithSet<T, TValue> = T | Set<TValue>;

const getEntries = <TKey extends Key, TValue>(collection: CollectionInput<TKey, TValue>) => {
  let entries;

  if (collection instanceof Map) entries = collection.entries();
  else entries = Object.entries(collection) as [TKey, TValue][];

  return Array.from(entries);
};

const getKeys = <TKey extends Key>(collection: CollectionInput<TKey, unknown>) => {
  let keys;

  if (collection instanceof Map) keys = collection.keys();
  else keys = Object.keys(collection) as TKey[];

  return Array.from(keys);
};

const getValues = <TKey extends Key, TValue>(
  collection: WithSet<CollectionInput<TKey, TValue>, TValue>,
) => {
  let values;

  if (collection instanceof Map || collection instanceof Set) values = collection.values();
  else values = Object.values(collection) as TValue[];

  return Array.from(values);
};

const getEnumValues = <T extends NonNullable<unknown>>(enumObj: T) =>
  Object.entries(enumObj).map(([key, value]) => ({ name: key, value }));

const omitUndefined = <T extends Obj>(payload: T) => {
  const definedEntries = Object.entries(payload).filter(([_, value]) => value !== undefined);

  return Object.fromEntries(definedEntries) as Partial<T>;
};

const mergeFlatFields = <T extends object>(
  data: Partial<T> | null | undefined,
  defaultValues: T,
) => {
  if (!data) return defaultValues;

  const isKey = createKeyGuard(defaultValues).predicate;

  const mergedData = cloneDeep(defaultValues);

  Object.keys(mergedData).forEach((key) => {
    if (!isKey(key)) return;

    const value = data[key];

    if (value != null && value !== '') mergedData[key] = value;
  });

  return mergedData;
};

export { getEntries, getEnumValues, getKeys, getValues, mergeFlatFields, omitUndefined };
